import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { useVisualizationStore } from '../store/visualizationStore';
import { VisualizationNode, VisualizationLink, getLinkEndpointId } from '../types/infrastructure';
import { AutoLayoutEngine, AutoLayoutConfig } from '../utils/autoLayout';

interface WebGLRendererProps {
  width: number;
  height: number;
  enableWebGL: boolean;
}

export const WebGLRenderer: React.FC<WebGLRendererProps> = ({ width, height, enableWebGL }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const nodeGroupRef = useRef<THREE.Group | null>(null);
  const linkGroupRef = useRef<THREE.Group | null>(null);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  
  const {
    nodes,
    links,
    viewport,
    selectedNode,
    showNodeTypes,
    showConnectionTypes,
    currentContext,
    selectNode
  } = useVisualizationStore();

  // Initialize Three.js scene
  const initializeScene = useCallback(() => {
    if (!mountRef.current || !enableWebGL) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf9f9f9);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
    camera.position.z = 1000;
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    // Groups for organizing objects
    const nodeGroup = new THREE.Group();
    const linkGroup = new THREE.Group();
    scene.add(nodeGroup);
    scene.add(linkGroup);
    nodeGroupRef.current = nodeGroup;
    linkGroupRef.current = linkGroup;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight.position.set(1, 1, 1);
    scene.add(ambientLight);
    scene.add(directionalLight);

    mountRef.current.appendChild(renderer.domElement);

    return { scene, camera, renderer };
  }, [width, height, enableWebGL]);

  // Get node color as THREE.Color
  const getNodeColor = (node: VisualizationNode): THREE.Color => {
    const colors = {
      site: 0x1f77b4,      // Blue
      pod: 0xff7f0e,       // Orange
      network: 0x17becf,   // Cyan
      rack: 0x2ca02c,      // Green
      device: 0xd62728,    // Red
      component: 0x9467bd  // Purple
    };
    return new THREE.Color(colors[node.type] || 0x808080);
  };

  // Get node size based on type and zoom
  const getNodeSize = (node: VisualizationNode): number => {
    const baseSizes = {
      site: 30,
      pod: 25,
      network: 22,
      rack: 20,
      device: 15,
      component: 10
    };
    return baseSizes[node.type] || 15;
  };

  // Apply autolayout with role-based positioning
  const applyAutoLayout = useCallback((visibleNodes: VisualizationNode[], visibleLinks: VisualizationLink[]): VisualizationNode[] => {
    // Only apply autolayout if enabled and for network-level views where we can see devices
    const hasDevices = visibleNodes.some(node => node.type === 'device');

    if (!hasDevices || !currentContext || visibleNodes.length < 3) {
      return visibleNodes; // Return nodes as-is for non-device views
    }

    const autoLayoutConfig: AutoLayoutConfig = {
      enableVerticalSeparation: true,
      layerSpacing: height / 4,
      nodeSpacing: width / 10,
      forceStrength: 0.3
    };

    const layoutEngine = new AutoLayoutEngine(visibleNodes, visibleLinks, autoLayoutConfig);
    return layoutEngine.applyRoleBasedLayout(width, height);
  }, [width, height, currentContext]);

  // Filter visible elements based on hierarchical context
  const getVisibleElements = useCallback(() => {
    // Start with nodes that match the current filters
    let visibleNodes = nodes.filter(node => 
      showNodeTypes.includes(node.type)
    );

    // Apply hierarchical context filtering
    if (currentContext) {
      const contextNode = nodes.find(n => n.id === currentContext);
      const isNetworkContext = contextNode?.type === 'network';
      
      visibleNodes = visibleNodes.filter(node => {
        // Direct children are always visible
        if (node.parent === currentContext) {
          return true;
        }
        
        // For network contexts, also show devices that belong to racks within this network
        if (isNetworkContext && node.type === 'device') {
          // Check if the device's rack belongs to this network
          const deviceRackParent = node.parent; // This should be a rack ID
          const rackNode = nodes.find(n => n.id === deviceRackParent);
          if (rackNode && rackNode.parent === currentContext) {
            return true;
          }
        }
        
        return false;
      });
    } else {
      // At the root level, show only top-level nodes (sites)
      visibleNodes = visibleNodes.filter(node => {
        // For sites, they should not have a parent or parent should be null/undefined
        return node.type === 'site' || !node.parent;
      });
    }

    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    
    // Smart link filtering: aggregate connections to the appropriate hierarchical level
    const visibleLinks: VisualizationLink[] = [];
    const connectionCounts = new Map<string, {link: VisualizationLink, count: number, totalBandwidth: number}>();

    for (const link of links) {
      if (!link.protocol || !showConnectionTypes.includes(link.protocol)) continue;

      const originalSource = getLinkEndpointId(link.source);
      const originalTarget = getLinkEndpointId(link.target);
      let sourceNode = originalSource;
      let targetNode = originalTarget;

      // If the link endpoints are not visible, try to find their visible ancestors
      if (!visibleNodeIds.has(sourceNode)) {
        // Find the visible ancestor of the source
        const sourceComponent = nodes.find(n => n.id === sourceNode);
        if (sourceComponent && sourceComponent.parent && visibleNodeIds.has(sourceComponent.parent)) {
          sourceNode = sourceComponent.parent;
        } else {
          continue; // Can't find visible source
        }
      }

      if (!visibleNodeIds.has(targetNode)) {
        // Find the visible ancestor of the target
        const targetComponent = nodes.find(n => n.id === targetNode);
        if (targetComponent && targetComponent.parent && visibleNodeIds.has(targetComponent.parent)) {
          targetNode = targetComponent.parent;
        } else {
          continue; // Can't find visible target
        }
      }

      // Only count links that should be aggregated to show count
      // This includes both device-to-device connections and component-to-component connections 
      // that cross between different devices
      let shouldCount = false;
      
      if (originalSource.includes('/') && originalTarget.includes('/')) {
        // Component-to-component connections: count if they cross devices
        const sourceDevice = originalSource.split('/')[0];
        const targetDevice = originalTarget.split('/')[0];
        shouldCount = sourceDevice !== targetDevice;
      } else if (!originalSource.includes('/') && !originalTarget.includes('/')) {
        // Device-to-device connections: always count these
        shouldCount = true;
      } else {
        // Mixed (one device, one component): this shouldn't happen in our current data model
        shouldCount = false;
      }

      // Aggregate connections between the same nodes
      const connectionKey = `${sourceNode}-${targetNode}`;
      const reverseKey = `${targetNode}-${sourceNode}`;
      const useKey = connectionCounts.has(connectionKey) ? connectionKey : 
                     connectionCounts.has(reverseKey) ? reverseKey : connectionKey;
      
      const linkBandwidth = link.bandwidth?.value || 0;
      
      if (connectionCounts.has(useKey)) {
        const existing = connectionCounts.get(useKey)!;
        if (shouldCount) {
          existing.count++;
        }
        existing.totalBandwidth += linkBandwidth;
      } else {
        connectionCounts.set(useKey, {
          link: {
            ...link,
            source: sourceNode,
            target: targetNode,
            id: `aggregated-${connectionCounts.size}`
          },
          count: shouldCount ? 1 : 0,
          totalBandwidth: linkBandwidth
        });
      }
    }

    // Convert aggregated connections to visible links with count metadata
    for (const {link, count, totalBandwidth} of Array.from(connectionCounts.values())) {
      visibleLinks.push({
        ...link,
        metadata: {
          ...link.metadata,
          aggregatedCount: count,
          totalBandwidth: count > 1 ? totalBandwidth : undefined
        },
        // Update bandwidth to show total if aggregated
        bandwidth: count > 1 && link.bandwidth ? {
          ...link.bandwidth,
          value: totalBandwidth
        } : link.bandwidth
      });
    }

    // Calculate component counts for each visible node
    const visibleNodesWithCounts = visibleNodes.map(node => {
      // Only calculate component counts for device nodes (not sites, pods, racks)
      let externalComponentCount = 0;
      
      if (node.type === 'device') {
        // Find all components within this device
        const nodeComponents = nodes.filter(n => n.parent === node.id);
        
        // For each component, check if it has external connections
        for (const component of nodeComponents) {
          const hasExternalConnection = links.some(link => {
            const linkSource = getLinkEndpointId(link.source);
            const linkTarget = getLinkEndpointId(link.target);
            const isComponentSource = linkSource === component.id;
            const isComponentTarget = linkTarget === component.id;

            if (isComponentSource || isComponentTarget) {
              // Check if the other endpoint is outside this device
              const otherEndpoint = isComponentSource ? linkTarget : linkSource;
              
              // External if other endpoint doesn't belong to the same parent device
              if (otherEndpoint.includes('/')) {
                const otherParent = otherEndpoint.split('/')[0];
                return otherParent !== node.id;
              } else {
                // Direct device connection
                return otherEndpoint !== node.id;
              }
            }
            return false;
          });
          
          if (hasExternalConnection) {
            externalComponentCount++;
          }
        }
      }
      
      return {
        ...node,
        metadata: {
          ...node.metadata,
          externalComponentCount
        }
      };
    });

    // Apply autolayout positioning to nodes
    const layoutedNodes = applyAutoLayout(visibleNodesWithCounts, visibleLinks);

    return { visibleNodes: layoutedNodes, visibleLinks };
  }, [nodes, links, currentContext, showNodeTypes, showConnectionTypes, applyAutoLayout]);

  // Create node geometries and materials
  const createNodeMeshes = useCallback((visibleNodes: VisualizationNode[]) => {
    if (!nodeGroupRef.current) return [];

    // Clear existing nodes
    nodeGroupRef.current.clear();

    const meshes: { mesh: THREE.Mesh; node: VisualizationNode }[] = [];

    visibleNodes.forEach(node => {
      const size = getNodeSize(node);
      const color = getNodeColor(node);
      
      // Use different geometries for different node types
      let geometry: THREE.BufferGeometry;
      switch (node.type) {
        case 'site':
          geometry = new THREE.BoxGeometry(size, size, size);
          break;
        case 'pod':
          geometry = new THREE.CylinderGeometry(size / 2, size / 2, size);
          break;
        case 'network':
          geometry = new THREE.TorusGeometry(size / 2, size / 6, 8, 16);
          break;
        case 'rack':
          geometry = new THREE.BoxGeometry(size, size * 1.5, size / 2);
          break;
        case 'device':
          geometry = new THREE.SphereGeometry(size / 2, 16, 12);
          break;
        case 'component':
          geometry = new THREE.OctahedronGeometry(size / 2);
          break;
        default:
          geometry = new THREE.SphereGeometry(size / 2, 8, 6);
      }

      const material = new THREE.MeshLambertMaterial({ 
        color,
        transparent: true,
        opacity: selectedNode === node.id ? 1.0 : 0.8
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(node.x, node.y, node.z || 0);
      mesh.userData = { node };

      // Add outline for selected node
      if (selectedNode === node.id) {
        const outlineGeometry = geometry.clone();
        const outlineMaterial = new THREE.MeshBasicMaterial({
          color: 0x000000,
          transparent: true,
          opacity: 0.3
        });
        const outline = new THREE.Mesh(outlineGeometry, outlineMaterial);
        outline.scale.setScalar(1.1);
        mesh.add(outline);
      }

      nodeGroupRef.current!.add(mesh);
      meshes.push({ mesh, node });
    });

    return meshes;
  }, [selectedNode]);

  // Create link geometries
  const createLinkMeshes = useCallback((visibleLinks: VisualizationLink[], visibleNodes: VisualizationNode[]) => {
    if (!linkGroupRef.current) return;

    // Clear existing links
    linkGroupRef.current.clear();

    const nodePositions = new Map(visibleNodes.map(n => [n.id, { x: n.x, y: n.y, z: n.z || 0 }]));

    visibleLinks.forEach(link => {
      const sourcePos = nodePositions.get(getLinkEndpointId(link.source));
      const targetPos = nodePositions.get(getLinkEndpointId(link.target));
      
      if (!sourcePos || !targetPos) return;

      const points = [
        new THREE.Vector3(sourcePos.x, sourcePos.y, sourcePos.z),
        new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z)
      ];

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      
      // Line width based on bandwidth
      const lineWidth = link.bandwidth ? Math.max(1, Math.log10(link.bandwidth.value)) : 2;
      
      const material = new THREE.LineBasicMaterial({
        color: 0x999999,
        transparent: true,
        opacity: 0.6,
        linewidth: lineWidth
      });

      const line = new THREE.Line(geometry, material);
      linkGroupRef.current!.add(line);
    });
  }, []);

  // Handle mouse events
  const handleMouseEvent = useCallback((event: MouseEvent, eventType: 'click' | 'move') => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return;

    const rect = rendererRef.current.domElement.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    
    const intersects = raycasterRef.current.intersectObjects(
      nodeGroupRef.current?.children || [],
      true
    );

    if (intersects.length > 0) {
      const intersected = intersects[0].object;
      const nodeData = intersected.userData?.node;
      
      if (nodeData) {
        if (eventType === 'click') {
          selectNode(nodeData.id === selectedNode ? null : nodeData.id);
        }
        // Removed hover functionality to prevent continuous redraw
      }
    }
  }, [selectedNode, selectNode]);

  // Update camera based on viewport
  const updateCamera = useCallback(() => {
    if (!cameraRef.current) return;

    const camera = cameraRef.current;
    camera.position.x = -viewport.x;
    camera.position.y = viewport.y;
    camera.position.z = 1000 / viewport.zoom;
    
    camera.updateProjectionMatrix();
  }, [viewport]);

  // Render loop
  const animate = useCallback(() => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

    updateCamera();
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    requestAnimationFrame(animate);
  }, [updateCamera]);

  // Main render effect
  useEffect(() => {
    if (!enableWebGL) return;

    const { visibleNodes, visibleLinks } = getVisibleElements();
    
    if (rendererRef.current && sceneRef.current) {
      createNodeMeshes(visibleNodes);
      createLinkMeshes(visibleLinks, visibleNodes);
    }
  }, [enableWebGL, getVisibleElements, createNodeMeshes, createLinkMeshes]);

  // Initialize scene effect
  useEffect(() => {
    if (!enableWebGL) return;

    const sceneObjects = initializeScene();
    if (!sceneObjects) return;

    // Add event listeners
    const canvas = rendererRef.current?.domElement;
    if (canvas) {
      const handleClick = (e: MouseEvent) => handleMouseEvent(e, 'click');
      const handleMouseMove = (e: MouseEvent) => handleMouseEvent(e, 'move');
      
      canvas.addEventListener('click', handleClick);
      canvas.addEventListener('mousemove', handleMouseMove);

      // Start animation loop
      animate();

      // Store mount reference for cleanup
      const currentMount = mountRef.current;

      return () => {
        canvas.removeEventListener('click', handleClick);
        canvas.removeEventListener('mousemove', handleMouseMove);
        
        if (currentMount && canvas) {
          currentMount.removeChild(canvas);
        }
        sceneObjects.renderer.dispose();
      };
    }
  }, [enableWebGL, initializeScene, handleMouseEvent, animate]);

  // Update renderer size
  useEffect(() => {
    if (!rendererRef.current || !cameraRef.current) return;

    rendererRef.current.setSize(width, height);
    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
  }, [width, height]);

  if (!enableWebGL) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-gray-500">WebGL rendering disabled</div>
      </div>
    );
  }

  return (
    <div 
      ref={mountRef} 
      className="webgl-renderer"
      style={{ width, height, overflow: 'hidden' }}
    />
  );
};